import { Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '.';
import { useEmailConfirmationMutation } from '../../../../api/registration/organic/emailConfirmation';
import Mail from '../../../../assets/icons/Mail';
import Button from '../../../../components/styledComponents/Button';
import StatusAlert from '../../../../components/styledComponents/StatusAlert';
import colors from '../../../../constants/colors';
import { StatusType } from '../../../../constants/enums';
import { useDirectAccessAnalytics } from '../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../store/SnackbarProvider';
import Snackbar from '../../../../components/Snackbar';

const EmailVerification = () => {
  const { formValues } = useOrganicRegistrationForm();
  const [showResentBanner, setShowResentBanner] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const sendEmailVerificationMutation = useEmailConfirmationMutation();
  const { enqueueSnackbar, state } = useSnackbarsContext();
  const navigate = useNavigate();
  const { track } = useDirectAccessAnalytics();

  useEffect(() => {
    if (showResentBanner) {
      track('User Messaged', {
        event_type: 'Messaged',
        location: 'Feature Card',
        message_type: 'Info',
        contents: "We've resent the email.",
      });
    }
  }, [showResentBanner]);

  useEffect(() => {
    const { email } = formValues;
    if (!email) {
      navigate('/register');
    }
  }, []);

  const handleResendClick = async () => {
    track('Verification Email Resent', {
      event_type: 'Link Accessed',
      location: 'Feature Card',
      registration_type: 'Organic',
      flow: 'Email',
    });
    if (resendCount < 5) {
      try {
        await sendEmailVerificationMutation.mutateAsync({
          email: formValues.email,
          firstName: formValues.firstName,
        });
        setShowResentBanner(true);
        setResendCount((resendCount) => resendCount + 1);
      } catch (err) {
        enqueueSnackbar({
          variant: 'error',
          key: 'email-verification-resend-error',
          content:
            'Something went wrong resending your email. Please try again.',
        });
      }
    }
  };

  return (
    <Stack
      direction="column"
      data-testid="organic-registration-3"
      sx={{
        alignItems: "center",
        px: { mobile: 2, tablet: 4 },
        backgroundColor: colors.csaaGray,
        pt: 3,
        height: 1
      }}>
      <Typography
        variant="h1"
        sx={{
          fontWeight: 500,
          fontSize: { mobile: 33, tablet: 44 },
          lineHeight: { mobile: '48px', desktop: '56px' },
        }}
      >
        Verification
      </Typography>
      <StatusAlert
        sx={{ maxWidth: 1030, my: 2 }}
        title={
          showResentBanner
            ? "We've resent the email."
            : 'Verification email sent'
        }
        text={
          showResentBanner ? (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '18px',
                fontFamily: 'Cabin',
              }}
            >
              Still didn&apos;t receive it?{' '}
              <Button
                disableRipple
                disableTouchRipple
                variant="text"
                data-testid="resend-email-link-button"
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  fontFamily: 'inherit',
                  px: 0,
                  py: 0,
                  width: 'fit-content',
                  alignSelf: 'flex-start',
                }}
                onClick={handleResendClick}
              >
                Resend email again
              </Button>
            </Typography>
          ) : undefined
        }
        open
        status={StatusType.INFO}
      />
      <Stack
        direction="column"
        sx={{
          width: 1,
          maxWidth: 1030
        }}>
        {state.snackbars.length > 0 &&
          state.snackbars.map((snack, idx) => (
            <Snackbar
              {...snack}
              key={snack.key}
              id={snack.key!}
              dataTestId={`${snack.key}-${idx}`}
            />
          ))}
      </Stack>
      {!showResentBanner && (
        <Stack
          direction="column"
          sx={{
            background: colors.csaaWhite,
            mt: { tablet: 3 },
            mb: { tablet: 25 },
            borderRadius: 2,
            p: { mobile: 2, tablet: 5 },
          }}
        >
          <Mail sx={{ alignSelf: 'center' }} />
          <Typography
            sx={{
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontSize: { mobile: 16, tablet: 18 },
              lineHeight: '24px',
              mt: 5,
            }}
          >
            The last step is to verify your email address.
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontSize: { mobile: 16, tablet: 18 },
              lineHeight: '24px',
              mt: 2,
            }}
          >
            We have sent instructions to the email address you provided. If you
            do not receive the verification email, select “Resend email.”
          </Typography>
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              mt: 5
            }}>
            <Button
              type="button"
              onClick={handleResendClick}
              data-testid="resend-email-button"
            >
              Resend email
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default EmailVerification;
